import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { getListings, CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/api";
import ListingCard from "../components/ListingCard";

const CATEGORY_HERO = {
  hotels: { img: "https://images.unsplash.com/photo-1749788873243-65087660f55c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400", overline: "№ 01", lead: "", body: "" },
  villas: { img: "https://images.unsplash.com/photo-1757439402296-000be181e38b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400", overline: "№ 02", lead: "", body: "" },
};

export default function CategoryPage() {
  const { slug } = useParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getListings({ category: slug }).then(d => {
      setListings(d);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "instant" });
    }).catch(() => setLoading(false));
  }, [slug]);

  if (!CATEGORY_LABELS[slug]) return <Navigate to="/" replace />;

  const hero = CATEGORY_HERO[slug];
  const chapterIdx = CATEGORY_ORDER.indexOf(slug);
  const nextCat = CATEGORY_ORDER[(chapterIdx + 1) % CATEGORY_ORDER.length];

  return (
    <div className="grain" data-testid={`category-page-${slug}`}>
      {/* Hero */}
      <section className="relative min-h-[85vh] w-full overflow-hidden">
        <div className="absolute inset-0 kenburns">
          <img src={hero.img} alt={CATEGORY_LABELS[slug]} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-[var(--ml-bg)]" />
        <div className="absolute inset-0 vignette" />

        <div className="relative z-10 container-ml min-h-[85vh] flex flex-col justify-end pb-20 md:pb-28">
          <div className="reveal-up">
            <p className="font-mono-ml text-[0.65rem] tracking-[0.32em] uppercase text-[var(--ml-gold)] mb-6">
              {hero.overline} · MenorcaLuxury
            </p>
            <h1 className="font-serif-ml text-7xl md:text-[10rem] lg:text-[14rem] leading-[0.82] tracking-[-0.03em] text-[var(--ml-text)]">
              {CATEGORY_LABELS[slug]}
            </h1>
            {hero.lead && (
              <p className="font-serif-ml italic text-2xl md:text-4xl text-[var(--ml-gold)] mt-8 max-w-3xl leading-snug">
                {hero.lead}
              </p>
            )}
            <a
              href="https://booking.travelchannelworld.com/en/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`category-reserve-btn-${slug}`}
              className="btn-ml-gold inline-block mt-10"
            >
              Reserve Now
            </a>
          </div>
        </div>
      </section>

      {/* Intro essay */}
      {hero.body && (
        <section className="container-ml py-20 md:py-32 grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 md:col-span-3">
            <p className="overline mb-4">An introduction</p>
            <p className="font-mono-ml text-[0.65rem] uppercase tracking-[0.25em] text-[var(--ml-text-muted)]">{listings.length} entries</p>
          </div>
          <div className="col-span-12 md:col-span-8 md:col-start-5">
            <p className="font-serif-ml text-2xl md:text-3xl leading-[1.3] text-[var(--ml-text-dim)] drop-cap">
              {hero.body}
            </p>
          </div>
        </section>
      )}

      {/* Listings grid */}
      <section className="container-ml pb-24 md:pb-32">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            {[0, 1, 2].map(i => (
              <div key={i} className="aspect-[3/4] bg-[var(--ml-surface)] animate-pulse" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <p className="text-center py-32 text-[var(--ml-text-dim)] font-serif-ml italic text-2xl">
            New entries arriving in the next issue.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
            {listings.map((l, i) => {
              // Asymmetric: every 3rd item spans 6 cols, others 4+4 → varied layout
              const span = i % 5 === 0
                ? "md:col-span-7"
                : i % 5 === 1
                ? "md:col-span-5"
                : i % 5 === 2
                ? "md:col-span-4"
                : i % 5 === 3
                ? "md:col-span-4"
                : "md:col-span-4";
              const offset = i % 2 === 1 ? "md:mt-16" : "";
              return (
                <div key={l.id || l.slug} className={`col-span-12 ${span} ${offset}`}>
                  <ListingCard listing={l} index={i} />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Next chapter */}
      <section className="border-t border-white/5">
        <Link to={`/category/${nextCat}`} data-testid={`next-cat-${nextCat}`} className="block group relative h-64 md:h-80 overflow-hidden">
          <img src={CATEGORY_HERO[nextCat].img} alt={CATEGORY_LABELS[nextCat]} className="absolute inset-0 w-full h-full object-cover moody-img" />
          <div className="absolute inset-0 bg-black/70 group-hover:bg-black/55 transition-all duration-700" />
          <div className="relative container-ml h-full flex flex-col justify-center">
            <p className="overline mb-4">Next · {CATEGORY_HERO[nextCat].overline}</p>
            <h2 className="font-serif-ml text-5xl md:text-8xl tracking-tight text-[var(--ml-text)] group-hover:text-[var(--ml-gold)] transition-colors leading-[0.9]">
              {CATEGORY_LABELS[nextCat]} <span className="text-[var(--ml-gold)]">→</span>
            </h2>
          </div>
        </Link>
      </section>
    </div>
  );
}
